import { useEffect, useState } from 'react';
import { runCompatibilityCheck } from '../utils/versionCheck';

/**
 * Build user-facing banner copy for a non-compatible result.
 * @param {{status: string, appVersion: string|null, backendVersion: string|null, ranges: string[]|null}} result
 * @returns {string}
 */
const messageFor = (result) => {
  const ranges = result.ranges ? result.ranges.join(', ') : '';
  if (result.status === 'incompatible') {
    return `This version of the app (${result.appVersion}) was built for backend versions ${ranges}, but the server is running ${result.backendVersion}. Some features may not work correctly.`;
  }
  if (result.status === 'probe-failed') {
    return `Could not verify backend compatibility. The app expects backend versions ${ranges}. Some features may not work correctly.`;
  }
  if (result.status === 'manifest-failed') {
    return 'Could not load version manifest. Backend compatibility cannot be verified.';
  }
  return '';
};

/**
 * Banner that runs a one-shot compatibility check on mount and renders a
 * dismissible warning if the frontend cannot confirm the backend is supported.
 * Renders nothing for compatible/dev statuses, or after the user dismisses it.
 */
const CompatibilityBanner = () => {
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    runCompatibilityCheck().then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!result || dismissed) return null;
  if (result.status === 'compatible' || result.status === 'dev') return null;

  return (
    <div
      role="alert"
      className="bg-yellow-500 text-black px-4 py-2 flex items-start justify-between gap-4"
    >
      <p className="text-sm">{messageFor(result)}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss compatibility warning"
        className="text-black font-bold text-lg leading-none px-2"
      >
        ×
      </button>
    </div>
  );
};

export default CompatibilityBanner;
